import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Lockup } from '@/components/Logo';
import { ENGAGEMENTS, inquiryHref } from '@/lib/engagements';

// Resources is a real destination, consistent across desktop and mobile.
const LINKS = [...ENGAGEMENTS, { to: '/about', label: 'About' }, { to: '/resources', label: 'Resources' }];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <header
      className="on-forest sticky top-0 z-50 border-b border-rule-dark bg-forest text-cream-light"
      onKeyDown={(event) => {
        if (open && event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          toggleRef.current?.focus();
        }
      }}
      onBlur={(event) => {
        // This is a navigation disclosure, not a modal: Tab can leave it.
        if (open && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <div className="container flex h-[72px] items-center justify-between gap-6">
        <Link to="/" className="flex shrink-0 items-center py-2" aria-label="ECMWorks home">
          <Lockup ground="dark" height={36} />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-6 xl:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              aria-current={isActive(l.to) ? 'page' : undefined}
              className={`text-small underline-offset-[0.4em] decoration-1 hover:underline ${
                isActive(l.to) ? 'underline' : ''
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link to={inquiryHref(pathname)} className="btn-cream ml-2 !min-h-[2.75rem] !py-2">
            Talk to us
          </Link>
        </nav>

        <button
          ref={toggleRef}
          type="button"
          className="-mr-2 inline-flex h-11 w-11 items-center justify-center xl:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-7 w-7" aria-hidden /> : <Menu className="h-7 w-7" aria-hidden />}
        </button>
      </div>

      <div id="mobile-menu" hidden={!open} className="max-h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain border-t border-rule-dark xl:hidden">
        <nav aria-label="Primary mobile" className="container flex flex-col py-3">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              aria-current={isActive(l.to) ? 'page' : undefined}
              className={`border-b border-rule-dark py-4 text-small ${isActive(l.to) ? 'underline underline-offset-[0.4em]' : ''}`}
            >
              {l.label}
            </Link>
          ))}
          <Link to="/tools" className="border-b border-rule-dark py-4 text-small">Free tools</Link>
          <Link to="/notes" className="border-b border-rule-dark py-4 text-small">Field notes</Link>
          <Link to={inquiryHref(pathname)} className="btn-cream my-5 w-full">
            Talk to us
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Nav;
